import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import type { Collab, CollabState } from "@/types/collab"

/**
 * K6 state badge — derives the visible state label + color token pair from
 * the collab's `state` (OKS-34/32) or `cta.kind` (legacy OKS-7 stub states).
 *
 * The single source of truth lives in `deriveStateBadge`; the visual
 * component just renders.
 *
 * **K6 tones (OKS-104) — Pencil-token-backed via `application.css`:**
 *   - gray   → `bg-state-neutral-fill text-state-neutral-fg`  (Pencil `EARsT`)
 *               Open, Assigned, In review, Declined.
 *   - green  → `bg-state-positive-fill text-state-positive-fg` (Pencil `ENW7j`)
 *               Accepted (cta.kind branch), Submitted, Completed.
 *   - orange → `bg-state-action-fill text-state-action-fg`    (Pencil `gDUhN`)
 *               Action needed, Ready to submit, Posted.
 *
 * These three slots resolve through CSS custom properties declared in
 * `app/frontend/entrypoints/application.css` (see the `OKS-104 — Pencil-token
 * -backed K6 state-badge tones` comment block there). Light + dark variants
 * are owned by the CSS layer; no `dark:` modifier is needed at the consumer.
 *
 * **Out-of-K6 tones — unchanged Tailwind palettes** (PRD §1 Out of v1):
 *   - blue        → `bg-blue-100 text-blue-900` …    (Accepted — OKS-34 state)
 *   - sky         → `bg-sky-100` …                   (Applied — OKS-32)
 *   - amber       → `bg-amber-100` …                 (Purchased / To ship)
 *   - indigo      → `bg-indigo-100` …                (Shipped — OKS-32)
 *   - teal        → `bg-teal-100` …                  (Delivered — OKS-32)
 *   - destructive → `bg-destructive/15` …            (Issue — OKS-34)
 *   - muted_strikethrough → `bg-muted line-through`  (Rejected — OKS-32)
 *
 * These keep their current Tailwind palettes pixel-equivalent to master
 * until Pencil publishes tokens for the Drop / Promotional alphabet.
 */

type BadgeTone =
  | "gray"
  | "blue"
  | "orange"
  | "green"
  | "destructive"
  | "sky"
  | "amber"
  | "indigo"
  | "teal"
  | "muted_strikethrough"

interface DerivedBadge {
  /** i18n key — under `collabs.state_pill.*` for OKS-34 states, or
   *  `creator_home.state_badge.*` for the legacy K6 stub states. */
  labelKey: string
  tone: BadgeTone
  /** Optional SR-only addendum for ambiguous badges per UX Spec A11y. */
  srSuffixKey?: string
}

/**
 * OKS-34 — state-driven badge mapping. When the backend emits a real
 * `collab.state` (any of the new alphabet), the badge reads directly from
 * that. Legacy OKS-7 stub paths (where state is `assigned` but cta.kind is
 * one of the simulation kinds like `buy_with_code`) fall through to the K6
 * cta.kind matrix.
 */
const STATE_BADGE_MAP: Record<CollabState, DerivedBadge> = {
  assigned: { labelKey: "collabs.state_pill.assigned", tone: "gray" },
  // OKS-32 — Drop creator-applied state. Sky tone (distinct from
  // OKS-34's "accepted" blue, which is audit-only on Drop).
  applied: { labelKey: "collabs.state_pill.applied", tone: "sky" },
  accepted: { labelKey: "collabs.state_pill.accepted", tone: "blue" },
  // OKS-32 — Drop terminal-on-application. Muted gray w/ strikethrough.
  rejected: {
    labelKey: "collabs.state_pill.rejected",
    tone: "muted_strikethrough",
  },
  // OKS-33 — Promotional brand "spotted the order" bucket. Amber
  // (paired with `to_ship` in the shipping arc).
  purchased: { labelKey: "collabs.state_pill.purchased", tone: "amber" },
  // OKS-32 — Drop / Promotional brand packing bucket. Amber.
  to_ship: { labelKey: "collabs.state_pill.to_ship", tone: "amber" },
  // OKS-32 — Drop in-transit. Indigo.
  shipped: { labelKey: "collabs.state_pill.shipped", tone: "indigo" },
  // OKS-32 — Drop pre-content (creator films + posts next). Teal.
  delivered: { labelKey: "collabs.state_pill.delivered", tone: "teal" },
  posted: { labelKey: "collabs.state_pill.posted", tone: "orange" },
  completed: { labelKey: "collabs.state_pill.completed", tone: "green" },
  // OKS-483 — deadline-lapse lifecycle. `expired` is terminal-but-visible
  // (neutral grey "EXPIRED" per UX A2); `reopen_requested` is the interim
  // "REOPEN REQUESTED" (blue, shield icon owned by the detail surface).
  expired: { labelKey: "collabs.state_pill.expired", tone: "gray" },
  reopen_requested: {
    labelKey: "collabs.state_pill.reopen_requested",
    tone: "blue",
  },
  // declined collabs are filtered server-side — included for type
  // exhaustiveness in case a future surface (history view) shows them.
  declined: { labelKey: "collabs.state_pill.declined", tone: "gray" },
  issue: { labelKey: "collabs.state_pill.issue", tone: "destructive" },
}

/**
 * deriveStateBadge — OKS-34 state-first + K6 cta.kind fallback.
 *
 * Order of precedence:
 *   1. If `collab.state` is one of the OKS-34 alphabet AND the cta.kind is
 *      one of the OKS-34 cta kinds (or `view_status` on issue), derive from
 *      `collab.state` directly. This is the production v1 path.
 *   2. Otherwise (legacy stub paths from OKS-7), keep the original K6
 *      cta.kind matrix.
 *
 * OKS-445 — the `submit` kind is now the submission-ready picker entry; it
 * drives the badge from `state` via the short-circuit (not the legacy OKS-7
 * "ready to submit" matrix path, which is removed). The resubmission
 * "Submitted" affordance is rendered separately by `<ResubmissionPill>`.
 */
export function deriveStateBadge(collab: Collab): DerivedBadge {
  const { cta, state } = collab

  // OKS-34/32 path — drive the badge from `state` when the cta.kind is
  // one of the OKS-34/32 kinds (or view_status on an issue row), or
  // when the row is in a Drop-only state (applied / to_ship / shipped /
  // delivered / rejected — OKS-32 NEW). Legacy open-assigned cards
  // (cta.kind=accept, state=assigned) flow through to the state map
  // for "Assigned" gray.
  if (
    // OKS-445 — `submit` (submission-ready picker entry) replaces the removed
    // `mark_posted` kind. It drives the badge from `state` (e.g. open
    // `accepted` → "Accepted") just as `mark_posted` did.
    cta.kind === "submit" ||
    cta.kind === "awaiting_review" ||
    cta.kind === "awaiting_brand" ||
    cta.kind === "to_ship_progress" ||
    cta.kind === "shipped_progress" ||
    state === "issue" ||
    state === "completed" ||
    state === "declined" ||
    state === "rejected" ||
    state === "applied" ||
    state === "to_ship" ||
    state === "shipped" ||
    state === "delivered" ||
    // OKS-483 — deadline-lapse states drive the badge straight from `state`
    // (cta.kind is request_reopen / reopen_requested, not in the list above).
    state === "expired" ||
    state === "reopen_requested"
  ) {
    return STATE_BADGE_MAP[state]
  }

  // OKS-32 — Drop `assigned` cards (cta.kind=apply with method=post +
  // event=apply) flow through to the state map for "Assigned" gray, the
  // same as OKS-34's Open assigned. Stub `apply` (legacy OKS-7) keeps
  // the cta.kind matrix path below.
  if (cta.kind === "apply" && cta.method === "post" && state === "assigned") {
    return STATE_BADGE_MAP[state]
  }

  // Open-type assigned (state=assigned, cta.kind=accept) — gray
  // "Assigned" pill takes precedence over the OKS-7 "Open" tone now that
  // we have real workflow data. Falls through to the state map for the
  // assigned/accepted branch.
  if (cta.kind === "accept" && (state === "assigned" || state === "accepted")) {
    return STATE_BADGE_MAP[state]
  }

  // Legacy OKS-7 cta.kind matrix (drop / promotional stubs + the
  // submit/view_status K5 resubmission path).
  switch (cta.kind) {
    case "apply":
      return { labelKey: "creator_home.state_badge.open", tone: "gray" }
    case "accept":
      return { labelKey: "creator_home.state_badge.accepted", tone: "green" }
    case "buy_with_code":
      return {
        labelKey: "creator_home.state_badge.action_needed",
        tone: "orange",
        srSuffixKey: "creator_home.state_badge.action_needed_sr",
      }
    case "view_status":
      return {
        labelKey: "creator_home.state_badge.in_review",
        tone: "gray",
      }
  }
  // `submit` (OKS-445 submission-ready picker entry) / `awaiting_review` are
  // filtered by the short-circuit above, so TypeScript narrows them out of
  // the switch discriminant by the time we reach this point — `submit` now
  // drives the badge from `state` (e.g. open `accepted`) rather than the
  // legacy OKS-7 "ready to submit" matrix path. The resubmission "Submitted"
  // pill is rendered separately by `<ResubmissionPill>` off
  // `submission.can_resubmit`. The fallback exists for runtime safety only.
  return STATE_BADGE_MAP[state]
}

const TONE_CLASSES: Record<BadgeTone, string> = {
  // OKS-104 — K6 tones resolve through Pencil-token-backed CSS vars in
  // `application.css`. Dark mode is owned by the CSS layer (the `.dark`
  // block overrides the var values); no `dark:` modifier needed here.
  gray: "bg-state-neutral-fill text-state-neutral-fg border-transparent",
  green: "bg-state-positive-fill text-state-positive-fg border-transparent",
  orange: "bg-state-action-fill text-state-action-fg border-transparent",
  // OKS-32/33/34 — out-of-K6 tones; raw Tailwind palettes preserved
  // pixel-equivalent until Pencil publishes Drop/Promotional tokens
  // (PRD §1 Out of v1). Do NOT migrate these without a Pencil-side
  // publish + a follow-up ticket.
  blue: "bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-100 border-transparent",
  destructive:
    "bg-destructive/15 text-foreground dark:bg-destructive/30 dark:text-white border-transparent",
  // OKS-32 — Drop tones (UX Spec §2.2).
  sky: "bg-sky-100 text-sky-900 dark:bg-sky-900/30 dark:text-sky-100 border-transparent",
  amber:
    "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100 border-transparent",
  indigo:
    "bg-indigo-100 text-indigo-900 dark:bg-indigo-900/30 dark:text-indigo-100 border-transparent",
  teal: "bg-teal-100 text-teal-900 dark:bg-teal-900/30 dark:text-teal-100 border-transparent",
  // Drop terminal `rejected` — muted with strikethrough text per UX
  // Spec §2.2 ("muted-strikethrough").
  muted_strikethrough:
    "bg-muted text-muted-foreground line-through border-transparent",
}

interface StateBadgeProps {
  collab: Collab
  className?: string
}

/** Visible state badge — tone-mapped, SR-augmented where appropriate. */
export function StateBadge({ collab, className }: StateBadgeProps) {
  const { t } = useTranslation()
  const derived = deriveStateBadge(collab)
  return (
    <Badge
      data-testid="collab-state-badge"
      data-tone={derived.tone}
      data-state={collab.state}
      className={cn(TONE_CLASSES[derived.tone], className)}
    >
      {t(derived.labelKey)}
      {derived.srSuffixKey && (
        <span className="sr-only"> {t(derived.srSuffixKey)}</span>
      )}
    </Badge>
  )
}

/**
 * ResubmissionPill — K5 secondary pill rendered next to `StateBadge` when
 * `submission.can_resubmit === true`. Outline Badge per UX Spec.
 */
export function ResubmissionPill() {
  const { t } = useTranslation()
  return (
    <Badge
      variant="outline"
      data-testid="collab-resubmission-pill"
      className="text-xs"
    >
      {t("creator_home.state_badge.can_resubmit")}
    </Badge>
  )
}
